/**
 * Presence Service
 * 
 * Uses Supabase Realtime Presence for zero-HTTP online status tracking.
 * Replaces HTTP heartbeat pings with WebSocket-based presence.
 * 
 * Features:
 * - Global presence channel with auto-reconnect
 * - Activity status tracking (active/idle)
 * - Cross-tab synchronization via BroadcastChannel
 * - Graceful fallback to HTTP heartbeat if Realtime unavailable
 */

import { supabase } from '@/integrations/supabase/client';
import { logger } from './logger';
import { getCachedUserId } from '@/contexts/AuthContext';
import type { RealtimeChannel } from '@supabase/supabase-js';

// ============= TYPES =============

interface PresenceState {
  sessionId: string;
  userId: string | null;
  deviceId: string;
  isActive: boolean;
  lastActivity: number;
  joinedAt: number;
}

type PresenceStatusCallback = (online: boolean, error?: Error) => void;

// ============= CONSTANTS =============

const CHANNEL_NAME = 'presence:global';
const ACTIVITY_TIMEOUT_MS = 60000; // 1 minute without activity = inactive
const RECONNECT_DELAY_MS = 5000;
const MAX_RECONNECT_ATTEMPTS = 5;
const BROADCAST_CHANNEL_NAME = 'presence-sync';

// ============= STATE =============

let channel: RealtimeChannel | null = null;
let presenceState: PresenceState | null = null;
let isConnected = false;
let reconnectAttempts = 0;
let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
let activityCheckInterval: ReturnType<typeof setInterval> | null = null;
let statusCallbacks: PresenceStatusCallback[] = [];
let broadcastChannel: BroadcastChannel | null = null;
let isLeaderTab = false;

// ============= HELPER FUNCTIONS =============

function getDeviceId(): string {
  let deviceId = localStorage.getItem('device_id');
  if (!deviceId) {
    deviceId = `device-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('device_id', deviceId);
  }
  return deviceId;
}

function getSessionId(): string {
  let sessionId = sessionStorage.getItem('presence_session_id');
  if (!sessionId) {
    sessionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('presence_session_id', sessionId);
  }
  return sessionId;
}

function notifyStatusChange(online: boolean, error?: Error): void {
  for (const callback of statusCallbacks) {
    try {
      callback(online, error);
    } catch (e) {
      logger.error('Presence status callback error', { error: e });
    }
  }
}

// ============= CROSS-TAB SYNCHRONIZATION =============

function initBroadcastChannel(): void {
  if (typeof BroadcastChannel === 'undefined') {
    // Fallback for older browsers - this tab is always leader
    isLeaderTab = true;
    return;
  }

  try {
    broadcastChannel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
    
    // Request leadership
    broadcastChannel.postMessage({ type: 'LEADER_REQUEST', tabId: getSessionId() });
    
    // Assume leadership after short delay if no response
    setTimeout(() => {
      if (!isLeaderTab) {
        isLeaderTab = true;
        broadcastChannel?.postMessage({ type: 'LEADER_CLAIMED', tabId: getSessionId() });
        logger.debug('Presence: This tab is leader');
      }
    }, 100);
    
    broadcastChannel.onmessage = (event) => {
      const { type, tabId } = event.data;
      
      if (type === 'LEADER_REQUEST' && isLeaderTab && tabId !== getSessionId()) {
        // Another tab requesting - tell them we're leader
        broadcastChannel?.postMessage({ type: 'LEADER_EXISTS', tabId: getSessionId() });
      }
      
      if (type === 'LEADER_EXISTS' && tabId !== getSessionId()) {
        // Another tab is already leader
        isLeaderTab = false;
      }
      
      if (type === 'LEADER_CLAIMED' && tabId !== getSessionId()) {
        // Another tab claimed leadership
        isLeaderTab = false;
      }
      
      if (type === 'PRESENCE_UPDATE') {
        // Sync presence state from leader
        if (!isLeaderTab && event.data.state) {
          presenceState = event.data.state;
        }
      }
    };
  } catch (e) {
    logger.warn('BroadcastChannel not available', { error: e });
    isLeaderTab = true;
  }
}

function broadcastPresenceUpdate(): void {
  if (broadcastChannel && isLeaderTab && presenceState) {
    try {
      broadcastChannel.postMessage({
        type: 'PRESENCE_UPDATE',
        state: presenceState,
      });
    } catch {
      // Ignore broadcast errors
    }
  }
}

// ============= PRESENCE CHANNEL =============

async function connectChannel(): Promise<boolean> {
  if (channel) {
    await channel.unsubscribe();
    channel = null;
  }

  const sessionId = getSessionId();
  const deviceId = getDeviceId();
  const userId = getCachedUserId();

  presenceState = {
    sessionId,
    userId,
    deviceId,
    isActive: true,
    lastActivity: Date.now(),
    joinedAt: Date.now(),
  };

  channel = supabase.channel(CHANNEL_NAME, {
    config: {
      presence: {
        key: sessionId,
      },
    },
  });

  return new Promise((resolve) => {
    if (!channel) {
      resolve(false);
      return;
    }

    channel
      .on('presence', { event: 'sync' }, () => {
        const state = channel?.presenceState();
        logger.debug('Presence sync', { 
          data: { onlineCount: Object.keys(state || {}).length },
          allowList: ['onlineCount'],
        });
      })
      .on('presence', { event: 'join' }, ({ key, newPresences }) => {
        logger.debug('Presence join', { 
          data: { key: key?.slice(0, 8), count: newPresences?.length },
          allowList: ['key', 'count'],
        });
      })
      .on('presence', { event: 'leave' }, ({ key }) => {
        logger.debug('Presence leave', { 
          data: { key: key?.slice(0, 8) },
          allowList: ['key'],
        });
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          isConnected = true;
          reconnectAttempts = 0;
          
          // Track presence
          if (presenceState) {
            await channel?.track({
              sessionId: presenceState.sessionId,
              userId: presenceState.userId?.slice(0, 8), // Only prefix for privacy
              deviceId: presenceState.deviceId.slice(0, 8),
              isActive: presenceState.isActive,
            });
          }
          
          logger.info('Presence connected', {
            data: { sessionId: sessionId.slice(0, 8) },
            allowList: ['sessionId'],
          });
          
          notifyStatusChange(true);
          resolve(true);
        } else if (status === 'CLOSED' || status === 'CHANNEL_ERROR') {
          isConnected = false;
          notifyStatusChange(false);
          scheduleReconnect();
          resolve(false);
        }
      });
  });
}

function scheduleReconnect(): void {
  if (reconnectTimeout) {
    clearTimeout(reconnectTimeout);
  }
  
  if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
    logger.warn('Presence: Max reconnect attempts reached');
    notifyStatusChange(false, new Error('Max reconnect attempts reached'));
    return;
  }
  
  const delay = RECONNECT_DELAY_MS * Math.pow(2, reconnectAttempts);
  reconnectAttempts++;
  
  logger.debug('Presence: Scheduling reconnect', {
    data: { attempt: reconnectAttempts, delayMs: delay },
    allowList: ['attempt', 'delayMs'],
  });
  
  reconnectTimeout = setTimeout(() => {
    connectChannel();
  }, delay);
}

// ============= ACTIVITY TRACKING =============

function startActivityTracking(): void {
  if (activityCheckInterval) {
    clearInterval(activityCheckInterval);
  }
  
  // Check activity status every 30 seconds
  activityCheckInterval = setInterval(() => {
    if (!presenceState || !channel) return;
    
    const wasActive = presenceState.isActive;
    const now = Date.now();
    presenceState.isActive = now - presenceState.lastActivity < ACTIVITY_TIMEOUT_MS;
    
    // Only track if status changed
    if (wasActive !== presenceState.isActive && isConnected) {
      channel.track({
        sessionId: presenceState.sessionId,
        userId: presenceState.userId?.slice(0, 8),
        deviceId: presenceState.deviceId.slice(0, 8),
        isActive: presenceState.isActive,
      });
      broadcastPresenceUpdate();
    }
  }, 30000);
}

function handleActivity(): void {
  if (!presenceState) return;
  presenceState.lastActivity = Date.now();
  
  // Update active status if was inactive
  if (!presenceState.isActive && isConnected && channel) {
    presenceState.isActive = true;
    channel.track({
      sessionId: presenceState.sessionId,
      userId: presenceState.userId?.slice(0, 8),
      deviceId: presenceState.deviceId.slice(0, 8),
      isActive: true,
    });
    broadcastPresenceUpdate();
  }
}

// ============= PUBLIC API =============

/**
 * Initialize presence service
 * Should be called once on app startup
 */
export async function initPresence(): Promise<boolean> {
  // Initialize cross-tab sync
  initBroadcastChannel();
  
  // Only leader tab connects to Realtime
  if (!isLeaderTab) {
    logger.debug('Presence: Not leader tab, skipping Realtime connection');
    return true;
  }
  
  // Connect to presence channel
  const connected = await connectChannel();
  
  if (connected) {
    // Start activity tracking
    startActivityTracking();
    
    // Activity listeners (throttled)
    let activityTimeout: ReturnType<typeof setTimeout> | null = null;
    const throttledActivity = () => {
      if (activityTimeout) return;
      activityTimeout = setTimeout(() => {
        handleActivity();
        activityTimeout = null;
      }, 5000); // Throttle to every 5 seconds
    };

    window.addEventListener('mousemove', throttledActivity, { passive: true });
    window.addEventListener('keydown', throttledActivity, { passive: true });
    window.addEventListener('click', throttledActivity, { passive: true });
    window.addEventListener('touchstart', throttledActivity, { passive: true });

    // Visibility change
    document.addEventListener('visibilitychange', () => {
      if (presenceState && channel && isConnected) {
        const isActive = !document.hidden;
        presenceState.isActive = isActive;
        if (isActive) {
          presenceState.lastActivity = Date.now();
        }
        channel.track({
          sessionId: presenceState.sessionId,
          userId: presenceState.userId?.slice(0, 8),
          deviceId: presenceState.deviceId.slice(0, 8),
          isActive,
        });
      }
    });

    // Cleanup on page unload
    window.addEventListener('beforeunload', () => {
      stopPresence();
    });
  }
  
  return connected;
}

/**
 * Stop presence service and cleanup
 */
export async function stopPresence(): Promise<void> {
  if (reconnectTimeout) {
    clearTimeout(reconnectTimeout);
    reconnectTimeout = null;
  }
  
  if (activityCheckInterval) {
    clearInterval(activityCheckInterval);
    activityCheckInterval = null;
  }
  
  if (channel) {
    await channel.untrack();
    await channel.unsubscribe();
    channel = null;
  }
  
  if (broadcastChannel) {
    broadcastChannel.close();
    broadcastChannel = null;
  }
  
  isConnected = false;
  presenceState = null;
  isLeaderTab = false;
  
  logger.debug('Presence stopped');
}

/**
 * Check if presence is currently connected
 */
export function isPresenceConnected(): boolean {
  return isConnected;
}

/**
 * Subscribe to presence status changes
 */
export function onPresenceStatusChange(callback: PresenceStatusCallback): () => void {
  statusCallbacks.push(callback);
  
  // Return unsubscribe function
  return () => {
    statusCallbacks = statusCallbacks.filter(cb => cb !== callback);
  };
}

/**
 * Get current online users count (approximate)
 */
export function getOnlineUsersCount(): number {
  if (!channel) return 0;
  const state = channel.presenceState();
  return Object.keys(state).length;
}

/**
 * Force update presence state
 */
export async function updatePresence(isActive: boolean): Promise<void> {
  if (!presenceState || !channel || !isConnected) return;
  
  presenceState.isActive = isActive;
  presenceState.lastActivity = Date.now();
  
  await channel.track({
    sessionId: presenceState.sessionId,
    userId: presenceState.userId?.slice(0, 8),
    deviceId: presenceState.deviceId.slice(0, 8),
    isActive,
  });
  
  broadcastPresenceUpdate();
}

export default {
  initPresence,
  stopPresence,
  isPresenceConnected,
  onPresenceStatusChange,
  getOnlineUsersCount,
  updatePresence,
};
